import React, { useRef,useState, useEffect } from "react";
import { useSelector } from "react-redux";
import useDialogOpenState from "@minohealth/common/hooks/useDialogOpenState";
import {
  isUserWhitelistedForModelParamsControls,
  MODEL_PARAMS_FORM_ID
} from "./utils";
import { IoClose, IoSettings } from "react-icons/io5";
import "./model-params-modal.css";
import { useChatContext } from "@/services/chat-provider";

export default function ModelParamsModal({ isOpen, onClose }) {
  const { useModelConfiguration } = useChatContext()

  const { user } = useSelector((state) => state.app.datastate);
  const [modelConfig, setModelConfig] = useModelConfiguration()
  const { temperature, topP, systemPrompt } = modelConfig
  const dialogRef = useRef(null);
  const [, setIsDialogOpen] = useDialogOpenState(dialogRef, { closeOnEscape: true });
  const [animationState, setAnimationState] = useState('');

  useEffect(() => {
    if (isOpen) {
      setIsDialogOpen(true);
      // Add entrance animation class after dialog opens
      setTimeout(() => {
        if (dialogRef.current) {
          dialogRef.current.classList.add('entering');
          setAnimationState('entering');
        }
      }, 10);
    } else {
      // Add exit animation class before closing
      if (dialogRef.current && dialogRef.current.open) {
        dialogRef.current.classList.add('exiting');
        setAnimationState('exiting');
        // Close dialog after animation completes
        setTimeout(() => {
          setIsDialogOpen(false);
          if (dialogRef.current) {
            dialogRef.current.classList.remove('entering', 'exiting');
            setAnimationState('');
          }
        }, 300); // Match the exit animation duration
      } else {
        setIsDialogOpen(false);
      }
    }
  }, [isOpen, setIsDialogOpen]);

  // Clean up animation classes when dialog closes
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleClose = () => {
      dialog.classList.remove('entering', 'exiting');
      setAnimationState('');
      onClose();
    };

    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  useEffect(() => {
    const form = document.getElementById(MODEL_PARAMS_FORM_ID);
    if (!(form instanceof HTMLFormElement)) return;

    const temperatureInput = form.elements["temperature"];
    const topPInput = form.elements["top-p"];
    const systemPromptInput = form.elements["system-prompt"];

    if (
      !(temperatureInput instanceof HTMLInputElement) ||
      !(topPInput instanceof HTMLInputElement) ||
      !(systemPromptInput instanceof HTMLTextAreaElement)
    ) return;

    if (temperature == null || isWithinRange(temperature, 0, 1)) {
      temperatureInput.setCustomValidity("");
    } else {
      temperatureInput.setCustomValidity("Temperature must be between 0 and 1");
    }

    if (topP == null || isWithinRange(topP, 0, 1)) {
      topPInput.setCustomValidity("");
    } else {
      topPInput.setCustomValidity("Top-p must be between 0 and 1");
    }

    if (systemPrompt == null || (systemPrompt.split(" ").length <= 1000)) {
      systemPromptInput.setCustomValidity("");
    } else {
      systemPromptInput.setCustomValidity("System prompt must be at most 1000 words");
    }
  }, [temperature, systemPrompt, topP]);

  if (!isUserWhitelistedForModelParamsControls(user?.email)) return null;

  return (
    <dialog ref={dialogRef} className="model-params-modal" onClose={onClose}>
      <div className="model-params-modal__content">
        <div className="model-params-modal__header">
          <div className="model-params-modal__title">
            <IoSettings className="model-params-modal__icon" />
            <h2>Model Parameters</h2>
          </div>
          <button
            type="button"
            className="model-params-modal__close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <IoClose />
          </button>
        </div>

        <div className="model-params-modal__body">
          <form id={MODEL_PARAMS_FORM_ID} className="model-params-form">
            <div className="model-params-form__section">
              <h3 className="model-params-form__section-title">Generation Settings</h3>
              
              <div className="model-params-form__row">
                <div className="model-params-form__field">
                  <label className="model-params-form__label">
                    <span className="model-params-form__label-text">Temperature</span>
                    <span className="model-params-form__label-description">
                      Controls randomness in responses (0.0 = deterministic, 1.0 = very random)
                    </span>
                  </label>
                  <input
                    type="number"
                    name="temperature"
                    className="model-params-form__input"
                    step={0.01}
                    min={0}
                    max={1}
                    defaultValue={temperature}
                    onChange={(ev) => {
                      setModelConfig(prev => ({
                        ...prev,
                        temperature: parseFloat(ev.target.value)
                      }))
                    }}
                    placeholder="0.7"
                  />
                </div>

                <div className="model-params-form__field">
                  <label className="model-params-form__label">
                    <span className="model-params-form__label-text">Top P</span>
                    <span className="model-params-form__label-description">
                      Controls diversity via nucleus sampling (0.1 = focused, 1.0 = diverse)
                    </span>
                  </label>
                  <input
                    type="number"
                    name="top-p"
                    className="model-params-form__input"
                    step={0.01}
                    min={0}
                    max={1}
                    defaultValue={topP}
                    onChange={(ev) => {
                      setModelConfig(prev => ({
                        ...prev,
                        topP: parseFloat(ev.target.value)
                      }))
                    }}
                    placeholder="0.9"
                  />
                </div>
              </div>
            </div>

            <div className="model-params-form__section">
              <h3 className="model-params-form__section-title">System Instructions</h3>
              
              <div className="model-params-form__field">
                <label className="model-params-form__label">
                  <span className="model-params-form__label-text">System Prompt</span>
                  <span className="model-params-form__label-description">
                    Custom instructions to guide the AI's behavior (max 1000 words)
                  </span>
                </label>
                <textarea
                  name="system-prompt"
                  className="model-params-form__textarea"
                  defaultValue={systemPrompt}
                  onChange={(ev) => {
                    setModelConfig(prev => ({
                      ...prev,
                      systemPrompt: ev.target.value
                    }))
                  }}
                  rows={6}
                  placeholder="Enter custom system instructions..."
                />
              </div>
            </div>
          </form>
        </div>

        <div className="model-params-modal__footer">
          <button
            type="button"
            className="model-params-modal__btn model-params-modal__btn--secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
}

/**
 * @param {number} value 
 * @param {number} min 
 * @param {number} max 
 */
function isWithinRange(value, min, max) {
  return value >= min && value <= max;
}